import {match} from 'ts-pattern';

import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';

import {composePath, pushGtmEvent} from 'shared/web';

import {PromptBridgeNotificationsFactoryParams} from '../types/PromptBridgeNotificationsFactoryParams';
import {PromptBridgeNotificationsFactoryResult} from '../types/PromptBridgeNotificationsFactoryResult';
import {isExtractDeliveryNoteDocumentMessageInput} from './isExtractDeliveryNoteDocumentMessageInput';
import {parseDeliveryNotePayload} from './parseDeliveryNotePayload';

export const getDeliveryNoteNotifications = (
  params: PromptBridgeNotificationsFactoryParams
): PromptBridgeNotificationsFactoryResult => {
  const doesNotificationExist = params.notifications.some(
    (notification) => notification.id === params.notificationId
  );

  const parsedJson = parseDeliveryNotePayload(params.payload);
  const payload = isExtractDeliveryNoteDocumentMessageInput(parsedJson) ? parsedJson : null;

  return match(params.status)
    .with('pending', 'processing', () => ({
      shouldShow: true,
      shouldUpdate: false,
      notificationProps: {
        id: params.notificationId,
        variant: 'info' as const,
        layout: 'detailed' as const,
        autoClose: true,
        isLoading: true,
        title: i18n.t('entity.documentExtraction.notifications.processingExtraction'),
        message:
          payload?.fileName ?? i18n.t('entity.documentExtraction.notifications.extractDocument'),
        secondaryMessage: `${i18n.t('general.labels.createdAt')}: ${params.createdAt}`,
      },
    }))
    .with('success', () => {
      if (doesNotificationExist) {
        return {
          shouldShow: false,
          shouldUpdate: true,
          updateProps: {
            autoClose: false,
            isLoading: false,
            variant: 'success' as const,
            title: i18n.t(
              'entity.documentExtraction.notifications.extractionCompletedSuccessfully'
            ),
            actions: [
              {
                type: 'button' as const,
                variant: 'primary' as const,
                title: i18n.t('general.actions.view'),
                onClick: () => {
                  pushGtmEvent({
                    event: 'button_click',
                    event_id: 'ai_tool_dn_toast_notification_view',
                    value: params.notificationId,
                  });

                  window.open(
                    composePath(warehouseRoutes.deliveryNoteDocumentPreview, {
                      params: {id: params.notificationId},
                      isSearchParamsPreserved: false,
                    }),
                    '_blank'
                  );

                  params.hideNotification(params.notificationId);
                },
              },
            ],
          },
        };
      }

      return {
        shouldShow: true,
        shouldUpdate: false,
        notificationProps: {
          id: params.notificationId,
          variant: 'success' as const,
          layout: 'detailed' as const,
          autoClose: false,
          title: i18n.t('entity.documentExtraction.notifications.extractionCompletedSuccessfully'),
          message:
            payload?.fileName ?? i18n.t('entity.documentExtraction.notifications.extractDocument'),
          secondaryMessage: `${i18n.t('general.labels.createdAt')}: ${params.createdAt}`,
          actions: [
            {
              type: 'button' as const,
              variant: 'primary' as const,
              title: i18n.t('general.actions.view'),
              onClick: () => {
                window.open(
                  composePath(warehouseRoutes.deliveryNoteDocumentPreview, {
                    params: {id: params.notificationId},
                    isSearchParamsPreserved: false,
                  }),
                  '_blank'
                );
                params.hideNotification(params.notificationId);
              },
            },
          ],
        },
      };
    })
    .with('error', () => {
      if (doesNotificationExist) {
        return {
          shouldShow: false,
          shouldUpdate: true,
          updateProps: {
            autoClose: false,
            isLoading: false,
            variant: 'error' as const,
            title: i18n.t('entity.documentExtraction.notifications.extractionFailed'),
          },
        };
      }

      return {
        shouldShow: true,
        shouldUpdate: false,
        notificationProps: {
          id: params.notificationId,
          variant: 'error' as const,
          layout: 'detailed' as const,
          autoClose: false,
          title: i18n.t('entity.documentExtraction.notifications.extractionFailed'),
          message:
            payload?.fileName ?? i18n.t('entity.documentExtraction.notifications.extractDocument'),
          secondaryMessage: `${i18n.t('general.labels.createdAt')}: ${params.createdAt}`,
        },
      };
    })
    .otherwise(() => ({
      shouldShow: false,
      shouldUpdate: false,
    }));
};
